import { slerp } from '../geo/vec3.js';
import { polygonArea, toVec3 } from '../geo/sphere.js';
import { detailLandmass } from '../art/coastline.js';
import { hashSeed } from '../art/coastline.js';
import { blocks } from './blocks.js';
import { partition } from './partition.js';
/**
 * Turns a region tree and a world's art into the actual shapes on the globe.
 *
 * Continents are authored and fixed. Everything inside them — countries, then
 * cities — is partitioned from importance weights, recursively, so borders move
 * when priorities change while the world stays recognisably itself.
 */
/** How many starting arrangements to try before settling for the least bad. */
const PARTITION_ATTEMPTS = 3;
export function layoutWorld(art, regions, roots, options = {}) {
    const maxDepth = options.maxDepth ?? 3;
    const territories = new Map();
    const unplaced = [];
    const landmasses = [...art.continents, ...art.islands];
    const byKey = new Map(landmasses.map((m) => [m.key, m]));
    // Regions bind to a landmass by key, which is stable across art revisions.
    // Anything unbound takes the next free one, largest first, so a new continent
    // lands somewhere sensible rather than nowhere.
    const taken = new Set();
    for (const id of roots) {
        const key = regions.get(id)?.artSlotKey;
        if (key && byKey.has(key))
            taken.add(key);
    }
    const free = landmasses.filter((m) => !taken.has(m.key));
    let nextFree = 0;
    for (const rootId of roots) {
        const region = regions.get(rootId);
        if (!region)
            continue;
        const key = region.artSlotKey && byKey.has(region.artSlotKey) ? region.artSlotKey : undefined;
        const mass = key ? byKey.get(key) : free[nextFree++];
        if (!mass) {
            unplaced.push({ id: rootId, reason: 'no landmass left on this world' });
            continue;
        }
        const detailed = detailLandmass(mass, art.seed, region.geometrySeed ?? 0);
        const ring = scaleRing(detailed.ring, region.geometryScale ?? 1);
        territories.set(rootId, {
            id: rootId,
            ring,
            level: 0,
            parentId: null,
            site: centroidOf(ring),
            weight: 0,
            // A continent's area was left at zero here, which is a real number that
            // happens to be wrong: anything reading it — label placement, for one —
            // concluded that every continent was too small to carry its own name.
            area: polygonArea(ring),
        });
        subdivide(rootId, ring, 1);
    }
    function bestPartition(parentRing, targets, parentId, previous, level, variant) {
        // Precision buys nothing on a cell the size of a town, and a planet full
        // of them is otherwise seconds of frozen screen.
        const fine = level <= 2;
        const budget = fine ? {} : { maxIterations: 320, tolerance: 0.1, acceptError: 0.3 };
        const warm = previous && previous.size > 0 ? { previous } : {};
        let best = null;
        let bestLost = Infinity;
        for (let attempt = 0; attempt < PARTITION_ATTEMPTS; attempt += 1) {
            const result = partition(parentRing, targets, {
                ...budget,
                seed: hashSeed(art.seed, attempt === 0 ? parentId : `${parentId}#${attempt}`, variant),
                // Only the first attempt is warm-started: if the previous solution is
                // what is jamming the cells together, reusing it again will jam them
                // the same way.
                ...(attempt === 0 ? warm : {}),
            });
            const lost = result.cells.filter((cell) => cell.ring.length < 3).length;
            if (lost === 0)
                return result;
            if (lost < bestLost) {
                best = result;
                bestLost = lost;
            }
        }
        return best;
    }
    function subdivide(parentId, parentRing, level) {
        if (level > maxDepth)
            return;
        const parent = regions.get(parentId);
        if (!parent)
            return;
        const children = parent.children
            .map((id) => regions.get(id))
            .filter((r) => r !== undefined);
        if (children.length === 0)
            return;
        const total = children.reduce((sum, c) => sum + Math.max(c.importanceWeight, 1e-6), 0);
        const targets = children.map((c) => ({
            id: c.id,
            share: Math.max(c.importanceWeight, 1e-6) / total,
        }));
        const previous = options.previous
            ? new Map(targets
                .map((t) => [t.id, options.previous?.get(t.id)])
                .filter((entry) => entry[1] !== undefined))
            : undefined;
        // Seeded from the parent, so a territory's layout depends only on where it
        // sits in the tree — the same structure gives the same map everywhere.
        //
        // Some seeds fail badly: with many children in one parent, a starting
        // arrangement can pin cells against each other until they collapse to
        // nothing, and a collapsed cell is a region that silently vanishes from the
        // map. Thirty-seven surahs in one juz lost two thirds of themselves this
        // way. So a handful of seeds are tried in a fixed order and the first clean
        // one wins — still deterministic, still warm-started, and no longer at the
        // mercy of which id a region happens to have.
        // The smallest places are laid out as blocks rather than argued over as
        // borders: thirty-seven surahs partitioned into one city came out as a
        // strangled pie chart of wedges, where what a city wants is plots.
        const asBlocks = children.every((child) => child.kind === 'district');
        const variant = variantOf(children);
        // Plots have no borders to argue over, so a new shape is a new order of plots.
        const dealt = variant
            ? [...targets].sort((a, b) => hashSeed(a.id, variant) - hashSeed(b.id, variant))
            : targets;
        const laid = asBlocks ? blocks(parentRing, dealt) : [];
        const result = laid.length === targets.length
            ? { cells: laid }
            : bestPartition(parentRing, targets, parentId, previous, level, variant);
        for (const cell of result.cells) {
            if (cell.ring.length < 3) {
                unplaced.push({ id: cell.id, reason: 'no room left in its parent' });
                continue;
            }
            territories.set(cell.id, {
                id: cell.id,
                ring: cell.ring,
                level,
                parentId,
                site: cell.site,
                weight: cell.weight,
                area: cell.area,
            });
            subdivide(cell.id, cell.ring, level + 1);
        }
    }
    return { territories, unplaced };
}
/**
 * A country's outline is argued out with its siblings, so asking one of them
 * for a new shape re-deals the whole parent. Children still on the original
 * shape add nothing, which keeps every untouched world exactly as it was.
 */
function variantOf(children) {
    return children
        .filter((c) => (c.geometrySeed ?? 0) !== 0)
        .map((c) => `${c.id}:${c.geometrySeed}`)
        .join(',');
}
/** Grows or shrinks a ring about its own centre, along the sphere. */
function scaleRing(ring, scale) {
    if (scale === 1)
        return ring;
    const centre = centroidOf(ring);
    return ring.map((v) => slerp(centre, v, scale));
}
function centroidOf(ring) {
    const sum = ring.reduce((acc, v) => ({ x: acc.x + v.x, y: acc.y + v.y, z: acc.z + v.z }), { x: 0, y: 0, z: 0 });
    const length = Math.hypot(sum.x, sum.y, sum.z) || 1;
    return { x: sum.x / length, y: sum.y / length, z: sum.z / length };
}
export { toVec3 };
//# sourceMappingURL=layout.js.map